import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Bell, Loader2, Send } from 'lucide-react';
import { TopBanner } from '@/components/TopBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { usePushStats, useSendPush } from '@/hooks/usePushCampaign';
import { useAuthStore } from '@/stores/auth.store';

export const Route = createFileRoute('/_authenticated/notificaciones')({
  component: PromosPage,
});

function PromosPage() {
  return (
    <>
      <TopBanner title="Promos" subtitle="Mandá una notificación push a tus jugadores" />
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <PromoComposer />
      </div>
    </>
  );
}

function PromoComposer() {
  const { data: stats, isLoading: statsLoading } = usePushStats();
  const sendMutation = useSendPush();
  const role = useAuthStore((s) => s.user?.role);
  const isScopedToOperator = role === 'admin' || role === 'master';

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');

  const canSend = body.trim().length > 0 && !sendMutation.isPending;

  const onSend = () => {
    if (!canSend) return;
    sendMutation.mutate(
      {
        title: title.trim() || undefined,
        body: body.trim(),
        url: url.trim() || undefined,
        target: 'all',
      },
      {
        onSuccess: () => {
          setTitle('');
          setBody('');
          setUrl('');
        },
      },
    );
  };

  return (
    <Card className="max-w-2xl">
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5" />
          Nueva promo
        </CardTitle>
        <Badge variant="secondary" className="flex w-fit items-center gap-1">
          <Bell className="h-3.5 w-3.5" />
          {statsLoading ? '…' : (stats?.subscriptions ?? 0)} dispositivo(s) suscripto(s)
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Título (opcional)</label>
          <Input
            className="h-11 lg:h-10"
            placeholder="¡Bono especial esta semana!"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Mensaje</label>
          <textarea
            className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="Tenés un bono esperándote, entrá y jugá..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">URL al hacer click (opcional)</label>
          <Input className="h-11 lg:h-10" placeholder="/widget" value={url} onChange={(e) => setUrl(e.target.value)} />
        </div>

        <p className="text-xs text-muted-foreground">
          {isScopedToOperator
            ? 'Se envía a todos los jugadores del operador (todos los cajeros).'
            : 'Se envía solo a los jugadores que vos cargaste.'}
        </p>

        <div className="flex justify-end">
          <Button className="h-11 w-full sm:w-auto lg:h-10" onClick={onSend} disabled={!canSend}>
            {sendMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Enviar a mis jugadores
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
